/** @jsxImportSource @emotion/react */

import { Box, useTheme, Button } from '@mui/material'
import { publishIndicatorBadge, thumbnailListItem } from './RiskStoryCard.styles'
import {
  addCloudfrontUrl,
  defaultRiskStoryCoverImage,
  generateStoryLink,
} from '@app/components/Risk/Story/Stories.utils'
import { Story } from '@app/components/Risk/types'
import { Badge } from '@app/components/Atoms/Badge'
import Share from '@mui/icons-material/Share'
import React from 'react'
import { useAtomValue } from 'jotai'
import { clientNameAtom } from '@app/state/clientsAtom'
import { useSnackbars } from '@app/contexts/SnackbarContext'
import { ChevronRight } from '@mui/icons-material'
import { RiskStoryShareDialog } from './RiskStoryShareDialog'

export const RiskStoryCard = ({ story, onClick }: { story: Story; onClick: () => void }) => {
  const theme = useTheme()
  const { displaySnackbar } = useSnackbars()
  const [shareDialogOpen, setShareDialogOpen] = React.useState(false)

  const totalSteps = story.chapters.reduce((acc, chapter) => acc + chapter.steps.length, 1)

  const handleShareClickOpen = (event: React.MouseEvent) => {
    event.stopPropagation()
    setShareDialogOpen(true)
  }
  const handleShareClickClose = (event: React.MouseEvent) => {
    event.stopPropagation()
    setShareDialogOpen(false)
  }

  const clientName = useAtomValue(clientNameAtom)
  const storyLink = generateStoryLink(story, clientName)

  const handleCopy = (text: string, message: string) => async () => {
    try {
      await navigator.clipboard.writeText(text)
      displaySnackbar({
        message,
        type: 'success',
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy text: ', err)
    }
  }

  return (
    <Box css={thumbnailListItem(theme)}>
      <Box className="thumbnail" onClick={onClick}>
        <img
          className="cover"
          src={addCloudfrontUrl(
            story?.coverImageUrl ? story?.coverImageUrl : defaultRiskStoryCoverImage,
          )}
          alt={story.title}
        />
        <Badge css={publishIndicatorBadge(story.visibility)}>{story.visibility}</Badge>
      </Box>
      <Box className="label">
        <Box className="title">{story.title}</Box>
        <Box className="description">{story.description}</Box>
        <Box className="chapterCount">
          {story.chapters.length} Chapters, {totalSteps} Steps
        </Box>
        <Button
          startIcon={<Share />}
          className="shareButton"
          onClick={(e) => handleShareClickOpen(e)}
        >
          Share
        </Button>
        <Button className="button" onClick={onClick}>
          {` View `}
          <ChevronRight />
        </Button>
      </Box>
      <RiskStoryShareDialog
        shareDialogOpen={shareDialogOpen}
        storyLink={storyLink}
        isStoryPublic={story.visibility == 'Public'}
        onClose={handleShareClickClose}
        onCopy={handleCopy}
      />
    </Box>
  )
}
